import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API } from '../App';

const STAGE_COLORS = {
  initial:    'var(--text3)',
  kyc:        '#f59e0b',
  loi:        '#3b82f6',
  spa:        '#8b5cf6',
  execution:  '#ec4899',
  settlement: '#22c55e',
  billing:    '#10b981',
  completed:  '#059669',
  cancelled:  '#ef4444',
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/dashboard')
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Lade Dashboard…</div>;

  const c = stats?.counts || {};
  const a = stats?.alerts || {};
  const byStage = stats?.deals_by_stage || {};
  const byLcStatus = stats?.lc_by_status || {};
  const byKyc = stats?.kyc_by_status || {};
  const recentDeals = stats?.recent_deals || [];

  const ENTITY_CARDS = [
    { label: 'Deals',          value: c.deals || 0,           sub: `${a.active_deals || 0} aktiv`,                    icon: '📁', color: '#22c55e', to: '/deals' },
    { label: 'Counterparties', value: c.counterparties || 0,  sub: `${byKyc.approved || 0} KYC approved`,             icon: '🏢', color: '#3b82f6', to: '/counterparties' },
    { label: 'LC / SBLC',      value: c.lc_sblc || 0,         sub: `${a.lc_expiring_7_days || 0} laufen in 7 Tagen ab`, icon: '🏦', color: '#f59e0b', to: '/lc-sblc' },
    { label: 'Bills of Lading', value: c.bills_of_lading || 0, sub: `${byLcStatus.draft || 0} Entwurf`,               icon: '🚢', color: '#a855f7', to: '/bill-of-lading' },
    { label: 'Demurrage',      value: c.demurrage || 0,        sub: `${a.demurrage_disputes || 0} offene Disputes`,   icon: '⚓', color: '#ef4444', to: '/demurrage' },
    { label: 'Payment Terms',  value: c.payment_terms || 0,   sub: '',                                                icon: '💳', color: '#06b6d4', to: '/payment-terms' },
  ];

  const totalDealStages = Object.values(byStage).reduce((s, v) => s + v, 0);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text1)', margin: 0 }}>⚡ Energy BPOS</h1>
        <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>Trade Finance Management Suite</p>
      </div>

      {/* Alert Bar */}
      {(a.lc_expiring_7_days > 0 || a.demurrage_disputes > 0) && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {a.lc_expiring_7_days > 0 && (
            <div style={{ flex: 1, minWidth: 220, padding: '10px 14px', borderRadius: 8,
              background: '#f59e0b15', border: '1px solid #f59e0b40', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>⏰</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#f59e0b' }}>
                  {a.lc_expiring_7_days} LC/SBLC läuft in 7 Tagen ab
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                  {a.lc_expiring_30_days || 0} in den nächsten 30 Tagen
                </div>
              </div>
              <Link to="/lc-sblc" style={{ marginLeft: 'auto', fontSize: 11, color: '#f59e0b', textDecoration: 'none' }}>→ anzeigen</Link>
            </div>
          )}
          {a.demurrage_disputes > 0 && (
            <div style={{ flex: 1, minWidth: 220, padding: '10px 14px', borderRadius: 8,
              background: '#ef444415', border: '1px solid #ef444440', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>
                  {a.demurrage_disputes} offene Demurrage-Disputes
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>Klärung erforderlich</div>
              </div>
              <Link to="/demurrage" style={{ marginLeft: 'auto', fontSize: 11, color: '#ef4444', textDecoration: 'none' }}>→ anzeigen</Link>
            </div>
          )}
        </div>
      )}

      {/* Entity Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
        {ENTITY_CARDS.map(card => (
          <Link key={card.label} to={card.to} style={{ textDecoration: 'none' }}>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderTop: `3px solid ${card.color}`, borderRadius: 10, padding: '16px 18px',
              transition: 'background .15s', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{card.icon}</div>
              <div style={{ fontSize: 30, fontWeight: 800, color: card.color }}>{card.value}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', marginTop: 2 }}>{card.label}</div>
              {card.sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{card.sub}</div>}
            </div>
          </Link>
        ))}
      </div>

      <div className="grid-2">
        {/* Deal Pipeline */}
        <div className="card">
          <div className="card-title">Deal Pipeline <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: 12 }}>{totalDealStages} gesamt</span></div>
          {totalDealStages === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 13, padding: '12px 0' }}>Noch keine Deals angelegt.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {Object.entries(byStage)
                .sort((a, b) => {
                  const order = ['initial','kyc','loi','spa','execution','settlement','billing','completed','cancelled'];
                  return order.indexOf(a[0]) - order.indexOf(b[0]);
                })
                .map(([stage, count]) => {
                  const color = STAGE_COLORS[stage] || 'var(--text3)';
                  const pct = Math.round((count / totalDealStages) * 100);
                  return (
                    <div key={stage}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                        <span style={{ color, fontWeight: 600, textTransform: 'capitalize' }}>{stage}</span>
                        <span style={{ color: 'var(--text3)' }}>{count}</span>
                      </div>
                      <div style={{ height: 6, background: 'var(--bg-surface)', borderRadius: 3 }}>
                        <div style={{ height: 6, width: `${pct}%`, background: color, borderRadius: 3, transition: 'width .4s' }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Recent Deals */}
        <div className="card">
          <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
            Neueste Deals
            <Link to="/deals" style={{ fontSize: 11, fontWeight: 400, color: 'var(--accent)', textDecoration: 'none' }}>alle anzeigen →</Link>
          </div>
          {recentDeals.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 13, padding: '12px 0' }}>Noch keine Deals.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {recentDeals.map(deal => {
                const color = STAGE_COLORS[deal.stage] || 'var(--text3)';
                return (
                  <div key={deal.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {deal.title}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{deal.reference}</div>
                    </div>
                    <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 4,
                      background: color + '22', color, fontWeight: 700, flexShrink: 0 }}>
                      {deal.stage}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* KYC Status + LC Status */}
      <div className="grid-2" style={{ marginTop: 16 }}>
        <div className="card">
          <div className="card-title">Counterparty KYC Status</div>
          {Object.keys(byKyc).length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 13 }}>Keine Counterparties angelegt.</div>
          ) : (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { key: 'pending',     label: 'Ausstehend', color: 'var(--text3)' },
                { key: 'in_progress', label: 'In Bearbeitung', color: '#f59e0b' },
                { key: 'approved',    label: 'Genehmigt', color: '#22c55e' },
                { key: 'rejected',    label: 'Abgelehnt', color: '#ef4444' },
                { key: 'expired',     label: 'Abgelaufen', color: '#a855f7' },
              ].map(s => (byKyc[s.key] ? (
                <div key={s.key} style={{ padding: '8px 14px', borderRadius: 8,
                  background: s.color + '15', border: `1px solid ${s.color}30`, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{byKyc[s.key]}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{s.label}</div>
                </div>
              ) : null))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title">LC/SBLC Status</div>
          {Object.keys(byLcStatus).length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 13 }}>Keine LCs angelegt.</div>
          ) : (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {Object.entries(byLcStatus).map(([status, count]) => {
                const colors = {
                  draft: 'var(--text3)', issued: '#3b82f6', advised: '#8b5cf6',
                  confirmed: '#22c55e', utilized: '#10b981', expired: '#f59e0b', cancelled: '#ef4444',
                };
                const color = colors[status] || 'var(--text3)';
                return (
                  <div key={status} style={{ padding: '8px 14px', borderRadius: 8,
                    background: color + '15', border: `1px solid ${color}30`, textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color }}>{count}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, textTransform: 'capitalize' }}>{status}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
